import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import api from "../lib/auth";

export default function DailyActivityLogPage() {
  const navigate = useNavigate();
  const { shiftId: paramShiftId } = useParams();
  const [searchParams] = useSearchParams();

  const shiftId = paramShiftId || searchParams.get("shift_id") || searchParams.get("shiftId");

  const [time, setTime] = useState(() => {
    return new Date().toLocaleTimeString("en-AU", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
      timeZone: "Australia/Sydney",
    });
  });

  const [note, setNote] = useState("");
  const [entries, setEntries] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);

  const normaliseEntries = (payload) => {
    const raw =
      payload?.entries ||
      payload?.activity_logs ||
      payload?.logs ||
      payload?.shift_logs ||
      payload?.data ||
      payload ||
      [];

    return Array.isArray(raw) ? raw : [];
  };

  const formatLogTime = (entry) => {
    const value =
      entry?.time ||
      entry?.log_time ||
      entry?.created_at ||
      entry?.timestamp ||
      entry?.date;

    if (!value) return "--:--";

    if (typeof value === "string" && /^\d{1,2}:\d{2}/.test(value)) {
      return value.slice(0, 5);
    }

    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "--:--";

    return d.toLocaleTimeString("en-AU", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
      timeZone: "Australia/Sydney",
    });
  };

  const loadEntries = async () => {
    if (!shiftId) return;

    setLoading(true);
    try {
      const res = await api.get(`/shifts/${shiftId}/activity-log`);
      setEntries(normaliseEntries(res?.data));
    } catch (err) {
      console.error("[DailyActivityLog] load failed", err);
      setEntries([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEntries();
  }, [shiftId]);

  const add = async () => {
    if (!shiftId || !note.trim()) return;

    setSaving(true);
    try {
      const payload = {
        time,
        note: note.trim(),
        description: note.trim(),
        activity: note.trim(),
      };

      const res = await api.post(`/shifts/${shiftId}/activity-log`, payload);

      const returnedEntries = normaliseEntries(res?.data);
      if (returnedEntries.length) {
        setEntries(returnedEntries);
      } else {
        await loadEntries();
      }

      setNote("");
    } catch (err) {
      console.error("[DailyActivityLog] save failed", err);
      alert("Could not save activity log entry. Check backend logs.");
    } finally {
      setSaving(false);
    }
  };

  const safeEntries = Array.isArray(entries) ? entries : [];

  return (
    <div className="min-h-screen bg-[#09090b] text-white p-8">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-start justify-between mb-5">
          <div>
            <h1 className="text-2xl font-bold">Daily Activity Log</h1>
            <p className="text-zinc-400">Hourly log for static shift.</p>
            <p className="text-xs text-zinc-500">Shift ID: {shiftId || "Not available"}</p>
          </div>

          <button
            onClick={() => navigate(-1)}
            className="px-4 py-3 rounded bg-zinc-800 hover:bg-zinc-700"
          >
            Back
          </button>
        </div>

        <div className="bg-[#18181b] border border-zinc-800 rounded-xl p-4 mb-4">
          <input
            type="time"
            value={time}
            onChange={(e) => setTime(e.target.value)}
            className="w-full mb-3 bg-[#090d14] border border-zinc-800 rounded-xl px-3 py-3 text-white"
          />

          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Example: Nothing to report. Site secure."
            className="w-full min-h-[70px] bg-[#090d14] border border-zinc-800 rounded-xl px-3 py-3 text-white"
          />

          <button
            disabled={saving || !shiftId || !note.trim()}
            onClick={add}
            className="mt-3 px-4 py-2 rounded bg-blue-600 hover:bg-blue-500 disabled:opacity-50"
          >
            {saving ? "Saving..." : "Add Log Entry"}
          </button>
        </div>

        <div className="bg-[#18181b] border border-zinc-800 rounded-xl p-4">
          <h2 className="font-semibold mb-3">Shift Log Entries</h2>

          {loading && <p className="text-zinc-500">Loading entries...</p>}

          {!loading && safeEntries.length === 0 && (
            <p className="text-zinc-500">No entries yet.</p>
          )}

          {!loading &&
            safeEntries.map((entry, index) => (
              <div
                key={entry?.id || entry?._id || index}
                className="border-t border-zinc-800 py-3 first:border-t-0"
              >
                <div className="text-sm text-zinc-400">{formatLogTime(entry)}</div>
                <div className="text-white whitespace-pre-wrap">
                  {entry?.note ||
                    entry?.description ||
                    entry?.activity ||
                    entry?.message ||
                    "No details provided."}
                </div>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
}
